import distance from "@turf/distance";
import type { LineString, MultiLineString, MultiPolygon, Point, Polygon, Position } from "geojson";
import { minBy, sum, sumBy, unzip } from "lodash";

/**
 * Pure geometry helpers for street-parcel feature computation.
 *
 * These functions are intended to align with the PostGIS behaviour the model was trained against. In many cases
 * turf offers a function that "does the same thing", but whose behaviour is subtly different in some important way.
 */

export const DEG_TO_RAD = Math.PI / 180;

/** Return every ring in a (multi)polygon. */
export const polygonParts = (geom: Polygon | MultiPolygon): Position[][][] => (geom.type === "MultiPolygon" ? geom.coordinates : [geom.coordinates]);

/** Return the perimeter of the given polygon (i.e. the sum of the lengths of its rings). */
export const polygonPerimeterMetres = (geom: Polygon | MultiPolygon): number =>
  sumBy(polygonParts(geom).flat(), (ring) => sum(ring.slice(1).map((point, i) => distance(ring[i], point, { units: "meters" }))));

/** Return the number of vertices in the given geometry. */
export const pointCount = (geom: Polygon | MultiPolygon): number => sumBy(polygonParts(geom).flat(), (ring) => ring.length);

/** Return the number of parts in the given geometry. */
export const partCount = (geom: Polygon | MultiPolygon): number => polygonParts(geom).length;

/** Return the number of interior rings (holes) in the given geometry. */
export const holeCount = (geom: Polygon | MultiPolygon): number => sumBy(polygonParts(geom), (rings) => rings.length - 1);

/** Return the coordinates of every part's outer ring (hole rings are excluded). */
const outerRingCoords = (geom: Polygon | MultiPolygon): Position[] => polygonParts(geom).flatMap((rings) => rings[0]);

/**
 * Return the 2D cross product of the vectors `o -> a` and `o -> b`.
 *
 * The sign of the returned value gives the turn direction of the path `o -> a -> b`:
 * - positive => counter-clockwise,
 * - negative => clockwise,
 * - zero => the three points are collinear
 */
const cross = (o: Position, a: Position, b: Position): number => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

/**
 * Return the convex chain through the given points.
 *
 * The convex chain is the subsequence of points, running from the first to the last, that bends
 * only counter-clockwise and has every other point on or to its left.
 */
const halfHull = (points: Position[]): Position[] => {
  const hull: Position[] = [];

  for (const point of points) {
    // in the situation:
    //
    //        B
    //       / \
    //      /   \
    //     A     C
    //
    // we have a clockwise turn A -> B -> C. this means that B lies on the interior side of the chain A -> C,
    // and so B can be excluded from this chain.
    while (hull.length >= 2 && cross(hull[hull.length - 2], hull[hull.length - 1], point) <= 0) hull.pop();
    hull.push(point);
  }
  hull.pop();
  return hull;
};

/** Return the convex hull of the given points. */
export function convexHull(points: Position[]): Position[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (sorted.length < 3) return sorted;

  // the left-to-right pass traces the hull's lower boundary, the reverse pass its upper
  return [...halfHull(sorted), ...halfHull([...sorted].reverse())];
}

/**
 * Return the side lengths (metres) of the minimum-area rectangle around the parcel.
 */
export function orientedRectangleSidesMetres(geom: Polygon | MultiPolygon): [number, number] | null {
  const hull = convexHull(outerRingCoords(geom));
  if (hull.length < 3) return null;

  // there is no turf package that calculates the oriented bounding box of a (multi)polygon in the same way
  // that postgis does, so we have to implement one ourselves here.

  // one candidate rectangle per hull edge: project the hull onto the edge direction (u) and its normal (v)
  const candidates = hull.map((a, i) => {
    const b = hull[(i + 1) % hull.length];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    const ux = (b[0] - a[0]) / len;
    const uy = (b[1] - a[1]) / len;
    const [us, vs] = unzip(
      hull.map(([x, y]) => {
        const dx = x - a[0];
        const dy = y - a[1];
        return [dx * ux + dy * uy, dy * ux - dx * uy];
      })
    );
    return { a, ux, uy, minU: Math.min(...us), maxU: Math.max(...us), minV: Math.min(...vs), maxV: Math.max(...vs) };
  });

  const best = minBy(candidates, (c) => (c.maxU - c.minU) * (c.maxV - c.minV));
  if (!best) return null;

  const corner = (u: number, v: number): Position => [best.a[0] + u * best.ux - v * best.uy, best.a[1] + u * best.uy + v * best.ux];
  const corners = [corner(best.minU, best.minV), corner(best.maxU, best.minV), corner(best.maxU, best.maxV)];
  return [distance(corners[0], corners[1], { units: "meters" }), distance(corners[1], corners[2], { units: "meters" })];
}

interface RingMoment {
  /** The unsigned planar area of the ring, in squared degrees. */
  area: number;

  /** The x (longitude) offset of the ring's area centroid, relative to the origin `o`. */
  cx: number;

  /** The y (latitude) offset of the ring's area centroid, relative to the origin `o`. */
  cy: number;
}

/**
 * Return the planar area and area centroid of the given ring, relative to the origin `o`.
 */
function ringMoment(ring: Position[], o: Position): RingMoment {
  let a2 = 0;
  let rx = 0;
  let ry = 0;
  for (let i = 0; i + 1 < ring.length; i++) {
    const x0 = ring[i][0] - o[0];
    const y0 = ring[i][1] - o[1];
    const x1 = ring[i + 1][0] - o[0];
    const y1 = ring[i + 1][1] - o[1];
    const crossProduct = x0 * y1 - x1 * y0;
    a2 += crossProduct;
    rx += (x0 + x1) * crossProduct;
    ry += (y0 + y1) * crossProduct;
  }
  return a2 === 0 ? { area: 0, cx: 0, cy: 0 } : { area: Math.abs(a2 / 2), cx: rx / (3 * a2), cy: ry / (3 * a2) };
}

/**
 * Return the area centroid of the given geometry, or `null` if it encloses no area.
 *
 * The area centroid is the centre of mass of the region the geometry encloses (holes contribute
 * no mass).
 */
export function areaCentroid(geom: Polygon | MultiPolygon): Point | null {
  const origin = polygonParts(geom)[0][0][0];
  const moments = polygonParts(geom).flatMap((rings) =>
    rings.map((ring, ringIndex) => {
      const { area, cx, cy } = ringMoment(ring, origin);
      // ring 0 is the shell; the rest are holes, whose moments subtract
      return { weight: ringIndex === 0 ? area : -area, cx, cy };
    })
  );
  const net = sumBy(moments, (m) => m.weight);
  if (net <= 0) return null;
  return {
    type: "Point",
    coordinates: [origin[0] + sumBy(moments, (m) => m.weight * m.cx) / net, origin[1] + sumBy(moments, (m) => m.weight * m.cy) / net],
  };
}

/** Return the LineString's making up the given (Multi)LineString. */
export const toLineStrings = (geom: LineString | MultiLineString): LineString[] =>
  geom.type === "LineString" ? [geom] : geom.coordinates.map((coordinates) => ({ type: "LineString", coordinates }));
